import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import type { UserRole } from '@/types';
import { PageSpinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { authService } from '@/services/auth.service';

interface ProtectedRouteProps {
  children: ReactNode;
  roles?: UserRole[];
  redirectTo?: string;
}

export function ProtectedRoute({ children, roles, redirectTo = '/login' }: ProtectedRouteProps) {
  const { isAuthenticated, loading, initialized, role, firebaseUser, appUser } = useAuth();
  const location = useLocation();

  if (!initialized || loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <PageSpinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (roles && role && !roles.includes(role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (firebaseUser && initialized && !loading && !appUser) {
    return (
      <div className="flex h-screen flex-col items-center justify-center p-4 text-center">
        <h1 className="text-xl font-bold text-slate-900 mb-2">Perfil não configurado</h1>
        <p className="text-slate-500 mb-6 max-w-sm mx-auto">
          Sua conta de acesso foi criada, mas o perfil de usuário ainda não foi configurado no sistema. Entre em contato com o suporte.
        </p>
        <Button onClick={() => authService.signOut()}>Sair</Button>
      </div>
    );
  }

  if (appUser && appUser.status !== 'active') {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}

interface PublicOnlyRouteProps {
  children: ReactNode;
}

export function PublicOnlyRoute({ children }: PublicOnlyRouteProps) {
  const { isAuthenticated, loading, initialized } = useAuth();

  if (!initialized || loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <PageSpinner />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
